import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {get} from "react-hook-form";

// Utils
import useMountedState from "../../hooks/useMountedState.js";
import {inViewport} from "../../utils/helpers.js";
import useScrollListener from "../../hooks/useScrollListener.js";

// Style
import {voice} from "../../style/components/typography.js";
import {pulseDelay} from "../../style/components/animations.js";
import {radius} from "../../style/components/variables.js";

const InputError = ({testId = "input", errors, name, atBottom = true}) => {
  const isMounted = useMountedState();

  const [inWindow, setInWindow] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);

  const {message} = errors ? get(errors, name) ?? {} : {};

  const ref = useRef();
  const disableTimeout = useRef();
  const hasChanged = useRef(false);
  const scrolling = useScrollListener(document.getElementById("modal") || window);

  useEffect(() => {
    if (isMounted() && ref.current && inViewport(ref.current)) setInWindow(true);
  }, [isMounted]);

  useEffect(() => {
    if (isMounted() && scrolling && ref.current) {
      hasChanged.current = true;
      const updated = inViewport(ref.current);
      if (updated !== inWindow) setInWindow(updated);
    }
  }, [isMounted, scrolling, inWindow, message]);

  useEffect(() => {
    if (inWindow && hasChanged.current) {
      if (disableTimeout.current) clearTimeout(disableTimeout.current);

      setShouldPulse(true);
      disableTimeout.current = setTimeout(() => setShouldPulse(false), 2000); // matches animation length
    }
  }, [inWindow]);

  return (
    message && (
      <ErrorText
        data-testid={`${testId}-error`}
        atBottom={atBottom}
        ref={ref}
        shouldPulse={shouldPulse}>
        {message}
      </ErrorText>
    )
  );
};

InputError.propTypes = {
  testId: PropTypes.string,
  errors: PropTypes.objectOf(PropTypes.any).isRequired,
  name: PropTypes.string.isRequired,
  atBottom: PropTypes.bool
};

// Style Overrides
const ErrorText = styled.span`
  ${voice.quiet};
  display: flex;
  justify-content: ${({atBottom}) => (atBottom ? "flex-end" : "flex-start")};
  position: ${({atBottom}) => (atBottom ? "absolute" : "relative")};
  top: ${({atBottom}) => (atBottom ? "100%" : "unset")};
  left: 0;
  line-height: 1.5;
  color: ${({theme}) => theme.error};
  border-radius: ${radius};

  ${({shouldPulse, theme}) =>
    shouldPulse &&
    css`
      animation: ${pulseDelay(theme.error, 12)} 7s infinite;
    `}
`;

export default InputError;
